/**
 * CountryStateCitySelect
 * Cascading country → state → city selectors with search.
 * - Shows country name only (no phone code)
 * - Search filter on all three dropdowns
 * - Auto-resolves stateIso2 from stateName when states load (fixes default not showing)
 */

import React, { useEffect, useRef, useState } from "react";
import { useCountries, useStates, useCities } from "../../features/geo/hooks";

export interface GeoSelection {
  countryIso2: string;
  countryName: string;
  stateIso2: string;
  stateName: string;
  cityName: string;
  phoneCode: string;
  currency: string;
  currencyName: string;
  currencySymbol: string;
}

interface Props {
  value: GeoSelection;
  onChange: (value: GeoSelection) => void;
  showCurrency?: boolean;
  requireCity?: boolean;
  className?: string;
  /** Renders fields on a dark background (e.g. admin modals) instead of the default light theme. */
  dark?: boolean;
}

// ── Searchable dropdown ────────────────────────────────────────────────────

function SearchableSelect({
  label,
  required,
  disabled,
  loading,
  placeholder,
  selected,
  options,
  onSelect,
  allowCustom,
  dark,
}: {
  label: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  placeholder: string;
  selected: string;
  options: { value: string; label: string }[];
  onSelect: (value: string, label: string) => void;
  // When true, a query that matches no option can still be committed as-is
  // (e.g. a city the bundled geo dataset doesn't have, like "Kochi"). Country/
  // state must stay dataset-only since publish-gate matching resolves those by
  // exact/aliased name -> ISO code.
  allowCustom?: boolean;
  dark?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const displayLabel = options.find((o) => o.value === selected)?.label ?? selected ?? "";

  return (
    <div ref={ref} className="relative">
      <label className={`block text-xs font-medium mb-1 ${dark ? "text-gray-400" : "text-gray-600"}`}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <button
        type="button"
        disabled={disabled || loading}
        onClick={() => { if (!disabled && !loading) { setOpen((o) => !o); setQuery(""); } }}
        className={`w-full flex items-center justify-between rounded-lg border px-3 py-2 text-sm text-left focus:outline-none focus:border-blue-500 transition-colors
          ${disabled || loading
            ? dark ? "opacity-50 cursor-not-allowed bg-gray-800/40 border-gray-700" : "opacity-50 cursor-not-allowed bg-gray-50 border-gray-200"
            : dark ? "bg-gray-800 border-gray-700 hover:border-gray-600 cursor-pointer" : "bg-white border-gray-300 hover:border-gray-400 cursor-pointer"}`}
      >
        <span className={displayLabel ? (dark ? "text-gray-100" : "text-gray-900") : (dark ? "text-gray-500" : "text-gray-400")}>
          {loading ? "Loading…" : displayLabel || placeholder}
        </span>
        <svg className={`w-4 h-4 flex-shrink-0 transition-transform ${dark ? "text-gray-500" : "text-gray-400"} ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className={`absolute z-50 mt-1 w-full rounded-lg shadow-lg overflow-hidden border ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
          <div className={`p-2 border-b ${dark ? "border-gray-700" : "border-gray-100"}`}>
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}…`}
              className={`w-full rounded-md border px-2 py-1.5 text-sm focus:outline-none focus:border-blue-500 ${dark ? "bg-gray-900 border-gray-700 text-gray-100 placeholder:text-gray-500" : "border-gray-300"}`}
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {allowCustom && query.trim() && !options.some((o) => o.label.toLowerCase() === query.trim().toLowerCase()) && (
              <button
                type="button"
                onClick={() => { onSelect(query.trim(), query.trim()); setOpen(false); setQuery(""); }}
                className={`w-full text-left px-3 py-2 text-sm transition-colors border-b ${dark ? "text-cyan-400 hover:bg-gray-700 border-gray-700" : "text-blue-700 hover:bg-blue-50 border-gray-100"}`}
              >
                Use "{query.trim()}" (not in list)
              </button>
            )}
            {filtered.length === 0 && !allowCustom ? (
              <div className={`px-3 py-2 text-xs ${dark ? "text-gray-500" : "text-gray-400"}`}>No results</div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { onSelect(opt.value, opt.label); setOpen(false); setQuery(""); }}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors
                    ${opt.value === selected
                      ? dark ? "bg-cyan-500/10 text-cyan-400 font-medium" : "bg-blue-50 text-blue-700 font-medium"
                      : dark ? "text-gray-200 hover:bg-gray-700" : "text-gray-800 hover:bg-blue-50"}`}
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

const CountryStateCitySelect: React.FC<Props> = ({
  value,
  onChange,
  showCurrency = true,
  requireCity = false,
  className = "",
  dark = false,
}) => {
  const { countries, loading: countriesLoading } = useCountries();
  const { states,    loading: statesLoading }    = useStates(value.countryIso2);
  const hasStates = states.length > 0;

  const cityCountry = value.countryIso2;
  const cityState   = hasStates ? value.stateIso2 : undefined;
  const { cities, loading: citiesLoading } = useCities(cityCountry, cityState);

  const selectedCountry = countries.find((c) => c.iso2 === value.countryIso2);
  const cityEnabled = !!value.countryIso2 && (!hasStates || !!value.stateIso2);

  // Auto-resolve stateIso2 from stateName when states first load.
  // This fixes the "Select state" default when editing a saved profile — the DB
  // stores only stateName, not stateIso2. We find the matching state and update
  // the parent once so the dropdown shows the correct default.
  useEffect(() => {
    if (!statesLoading && states.length > 0 && value.stateName && !value.stateIso2) {
      const match = states.find(
        (s) => s.name.toLowerCase() === value.stateName.toLowerCase()
      );
      if (match) {
        onChange({ ...value, stateIso2: match.isoCode });
      }
    }
  // Only run when states load or stateName changes — not on every value change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statesLoading, states.length, value.stateName]);

  const handleCountrySelect = (iso2: string, name: string) => {
    const c = countries.find((x) => x.iso2 === iso2);
    onChange({
      countryIso2: iso2,
      countryName: c?.name ?? name,
      stateIso2: "",
      stateName: "",
      cityName: "",
      phoneCode: c?.phonecode ? `+${c.phonecode}` : "",
      currency: c?.currency ?? "",
      currencyName: c?.currency_name ?? "",
      currencySymbol: c?.currency_symbol ?? "",
    });
  };

  const handleStateSelect = (iso2: string, name: string) => {
    onChange({ ...value, stateIso2: iso2, stateName: name, cityName: "" });
  };

  const handleCitySelect = (cityName: string) => {
    onChange({ ...value, cityName });
  };

  const countryOptions = countries.map((c) => ({ value: c.iso2, label: `${c.flag} ${c.name}` }));
  const stateOptions   = states.map((s) => ({ value: s.isoCode, label: s.name }));
  const cityOptions    = cities.map((c) => ({ value: c.name, label: c.name }));

  return (
    <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${className}`}>
      {/* Country */}
      <div className="sm:col-span-2">
        <SearchableSelect
          label="Country"
          required
          loading={countriesLoading}
          placeholder="Select country"
          selected={value.countryIso2}
          options={countryOptions}
          onSelect={handleCountrySelect}
          dark={dark}
        />
        {showCurrency && selectedCountry && (
          <p className={`mt-1 text-xs ${dark ? "text-gray-500" : "text-gray-500"}`}>
            Currency: {selectedCountry.currency} ({selectedCountry.currency_symbol})
          </p>
        )}
      </div>

      {/* State — only when country has states */}
      {(hasStates || statesLoading) && (
        <div>
          <SearchableSelect
            label="State / Province"
            loading={statesLoading}
            disabled={!value.countryIso2}
            placeholder="Select state"
            selected={value.stateIso2}
            options={stateOptions}
            onSelect={handleStateSelect}
            dark={dark}
          />
        </div>
      )}

      {/* City */}
      <div className={hasStates || statesLoading ? "" : "sm:col-span-2"}>
        {cities.length > 0 ? (
          <SearchableSelect
            label="City"
            required={requireCity}
            loading={citiesLoading}
            disabled={!cityEnabled}
            placeholder="Select or type city"
            selected={value.cityName}
            options={cityOptions}
            onSelect={handleCitySelect}
            allowCustom
            dark={dark}
          />
        ) : (
          <div>
            <label className={`block text-xs font-medium mb-1 ${dark ? "text-gray-400" : "text-gray-600"}`}>
              City {requireCity && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={value.cityName}
              onChange={(e) => onChange({ ...value, cityName: e.target.value })}
              placeholder={citiesLoading ? "Loading…" : "Type city name"}
              required={requireCity}
              disabled={!cityEnabled}
              className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-50 ${
                dark ? "bg-gray-800 border-gray-700 text-gray-100 placeholder:text-gray-500" : "border-gray-300"
              }`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CountryStateCitySelect;
